/**
 * Invoice type → which relation/field is the bill-to.
 * All 4 bill-to kinds: customer | insurance_client | insurance_company | service_provider
 * Backend types: insurance | service_provider | customer | insurance_customer | insurance_company_monthly | service_provider_monthly | service_provider_to_darb
 */
const BILL_TO_BY_INVOICE_TYPE: Record<string, 'customer' | 'insurance_client' | 'insurance_company' | 'service_provider'> = {
  customer: 'customer',
  service_provider_to_darb: 'service_provider', // invoice from service provider to Darb → bill-to is the service provider
  insurance_customer: 'insurance_client',
  insurance: 'insurance_company',
  insurance_company_monthly: 'insurance_company',
  service_provider: 'service_provider',
  service_provider_monthly: 'service_provider',
};

export type BillToResult = {
  name: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  vatNumber: string | null;
  policyNumber: string | null;
  vinNumber: string | null;
  insuranceCompany: string | null;
};

/**
 * Derive bill-to from invoice data **by invoice type** (all 4 types):
 * - customer → customer data (contactPerson / companyName or customerName)
 * - insurance_customer → insurance client data (chassis/VIN as primary, then client name)
 * - insurance / insurance_company_monthly → insurance company name only
 * - service_provider / service_provider_monthly / service_provider_to_darb → service provider data
 * Fallback: use first populated relation if type is unknown or relation missing.
 */
export function getBillToFromInvoice(invoice: any): BillToResult {
  const attrs = invoice?.attributes ?? invoice;
  const invoiceType = attrs.invoiceType ?? 'customer';

  const insuranceClient = attrs.insuranceClient?.data?.attributes ?? attrs.insuranceClient?.data ?? attrs.insuranceClient;
  const customer = attrs.customer?.data?.attributes ?? attrs.customer?.data ?? attrs.customer;
  const serviceProvider = attrs.serviceProvider?.data?.attributes ?? attrs.serviceProvider?.data ?? attrs.serviceProvider;

  const billToKind = BILL_TO_BY_INVOICE_TYPE[invoiceType];

  // 1) Insurance company (invoice billed to company) → company name only
  if (billToKind === 'insurance_company') {
    const fromAttr = attrs.insuranceCompany ?? null;
    const fromClient = insuranceClient
      ? (insuranceClient.insuranceCompany?.data?.attributes?.name ?? insuranceClient.insuranceCompany?.data?.name ?? insuranceClient.insuranceCompany?.name ?? (typeof insuranceClient.insuranceCompany === 'object' ? null : insuranceClient.insuranceCompany))
      : null;
    const companyName = fromAttr ?? fromClient ?? null;
    return {
      name: companyName,
      address: null,
      phone: null,
      email: null,
      vatNumber: null,
      policyNumber: null,
      vinNumber: null,
      insuranceCompany: companyName,
    };
  }

  // 2) Customer (or service_provider_to_darb) → customer relation or customerName
  if (billToKind === 'customer') {
    if (customer) {
      return {
        name: customer.contactPerson ?? customer.companyName ?? null,
        address: customer.address ?? null,
        phone: customer.phone ?? customer.mobile ?? null,
        email: customer.email ?? null,
        vatNumber: customer.taxNumber ?? null,
        policyNumber: customer.policyNumber ?? null,
        vinNumber: customer.vinNumber ?? null,
        insuranceCompany: null,
      };
    }
    return {
      name: attrs.customerName ?? null,
      address: null,
      phone: null,
      email: null,
      vatNumber: null,
      policyNumber: null,
      vinNumber: null,
      insuranceCompany: null,
    };
  }

  // 3) Insurance client → show client chassis number (VIN) as primary, then client name and details
  if (billToKind === 'insurance_client' && insuranceClient) {
    const insCompany = insuranceClient.insuranceCompany?.data?.attributes ?? insuranceClient.insuranceCompany?.data ?? insuranceClient.insuranceCompany;
    const clientName = insuranceClient.customerName ?? insuranceClient.companyName ?? insuranceClient.contactPerson ?? null;
    const chassis = insuranceClient.vinNumber ?? null;
    // Primary display: chassis number (user asked "show client chassis number"); fallback to client name
    const name = chassis ? `Chassis: ${chassis}` : clientName;
    return {
      name: name || null,
      address: insuranceClient.address ?? null,
      phone: insuranceClient.phoneNumber ?? insuranceClient.phone ?? null,
      email: insuranceClient.email ?? null,
      vatNumber: insuranceClient.vatNumber ?? insCompany?.vatNumber ?? null,
      policyNumber: insuranceClient.policyNumber ?? null,
      vinNumber: insuranceClient.vinNumber ?? null,
      insuranceCompany: insCompany?.name ?? attrs.insuranceCompany ?? null,
    };
  }

  // 4) Service provider → service provider data
  if (billToKind === 'service_provider' && serviceProvider) {
    return {
      name: serviceProvider.name ?? null,
      address: serviceProvider.address ?? null,
      phone: serviceProvider.phone ?? null,
      email: serviceProvider.email ?? null,
      vatNumber: null,
      policyNumber: null,
      vinNumber: null,
      insuranceCompany: null,
    };
  }

  // Fallback: use first populated relation (for unknown types or missing data)
  if (insuranceClient) {
    const insCompany = insuranceClient.insuranceCompany?.data?.attributes ?? insuranceClient.insuranceCompany?.data ?? insuranceClient.insuranceCompany;
    const clientName = insuranceClient.customerName ?? insuranceClient.companyName ?? insuranceClient.contactPerson ?? null;
    const chassis = insuranceClient.vinNumber ?? null;
    const name = chassis ? `Chassis: ${chassis}` : clientName;
    return {
      name: name ?? null,
      address: insuranceClient.address ?? null,
      phone: insuranceClient.phoneNumber ?? insuranceClient.phone ?? null,
      email: insuranceClient.email ?? null,
      vatNumber: insuranceClient.vatNumber ?? insCompany?.vatNumber ?? null,
      policyNumber: insuranceClient.policyNumber ?? null,
      vinNumber: insuranceClient.vinNumber ?? null,
      insuranceCompany: insCompany?.name ?? attrs.insuranceCompany ?? null,
    };
  }
  if (customer) {
    return {
      name: customer.contactPerson ?? customer.companyName ?? null,
      address: customer.address ?? null,
      phone: customer.phone ?? customer.mobile ?? null,
      email: customer.email ?? null,
      vatNumber: customer.taxNumber ?? null,
      policyNumber: customer.policyNumber ?? null,
      vinNumber: customer.vinNumber ?? null,
      insuranceCompany: null,
    };
  }
  if (serviceProvider) {
    return {
      name: serviceProvider.name ?? null,
      address: serviceProvider.address ?? null,
      phone: serviceProvider.phone ?? null,
      email: serviceProvider.email ?? null,
      vatNumber: null,
      policyNumber: null,
      vinNumber: null,
      insuranceCompany: null,
    };
  }
  return {
    name: attrs.customerName ?? attrs.insuranceCompany ?? null,
    address: null,
    phone: null,
    email: null,
    vatNumber: null,
    policyNumber: null,
    vinNumber: null,
    insuranceCompany: attrs.insuranceCompany ?? null,
  };
}
